/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import solutionComposerDataProviderQueryParameters from '@jira/servicedesk__solution-composer/src/__generated__/SolutionComposerDataProviderQuery$parameters.ts';
import type { SolutionComposerDataProviderQuery } from '@jira/servicedesk__solution-composer/src/__generated__/SolutionComposerDataProviderQuery.graphql.ts';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JiraProjectAri, JiraWorkspaceAri } from '@atlassian/ari/jira';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

type GettingStartedEntryRootQueries = {
	SolutionComposerDataProviderQuery?: SolutionComposerDataProviderQuery;
};

function numericProjectIdForPreload(
	query: EntryPointRouteParams['context']['query'],
): string | null {
	const fromQuery =
		query?.selectedProjectId || query?.pid || query?.projectId || query?.['project.id'];
	if (fromQuery != null && fromQuery !== '' && /^\d+$/.test(String(fromQuery))) {
		return String(fromQuery);
	}
	return null;
}

type GettingStartedEntryPointComponent = EntryPointComponent<
	GettingStartedEntryRootQueries,
	{},
	{},
	{}
>;

export const gettingStartedEntryPoint: EntryPoint<
	GettingStartedEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<GettingStartedEntryPointComponent>(
		/* webpackChunkName: "async-servicedesk-getting-started-page", jiraSpaEntry: "async-servicedesk-getting-started-page" */ './index.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		if (!fg('solution_composer_master')) {
			return {};
		}
		const { cloudId, activationId } = entryPointParams.tenantContext;
		if (cloudId == null || activationId == null) {
			return {};
		}
		const { query, match } = entryPointParams.context;
		const projectId = numericProjectIdForPreload(query);
		if (projectId == null) {
			return {};
		}
		const projectKey = match?.params?.projectKey;
		const projectAri = JiraProjectAri.create({
			siteId: cloudId,
			projectId,
		}).toString();
		const workspaceAri = JiraWorkspaceAri.create({
			siteId: cloudId,
			activationId,
		}).toString();
		if (!projectKey) {
			return {};
		}

		return {
			queries: {
				SolutionComposerDataProviderQuery: {
					parameters: solutionComposerDataProviderQueryParameters,
					variables: {
						cloudId,
						cloudIdString: cloudId,
						projectAri,
						projectId,
						projectIdForHelpCenter: projectId,
						workspaceAri,
						projectKey,
					},
					options: { fetchPolicy: 'store-or-network' as const },
				},
			},
		};
	},
});
